/**
 * activity-log.js — Cloud activity log service.
 *
 * Writes normalized ActivityItem entries to the `activity_log` Supabase table.
 * Reads are intentionally not implemented here — reads go through useActivityFeed
 * (local sources + buildActivityFeed) until realtime is activated.
 *
 * Design principles:
 *   - Logging is best-effort: failures NEVER break the main write path.
 *   - Append-only: no updates, no deletes.
 *   - Idempotent: source_id + activity_type unique constraint prevents duplicates.
 *
 * Activation checklist:
 *   1. Run supabase/migrations/20260510_activity_log.sql
 *   2. Set LOGGING_ENABLED = true below
 *   3. Uncomment REALTIME_ENABLED when Supabase Realtime subscription is wired up
 *
 * NOT YET ACTIVE — architecture prepared, activation pending.
 * All call sites are already wired up; flipping the flag enables the system.
 */

import { supabase } from "./supabase";

// Set to true once the activity_log migration has run in production
const LOGGING_ENABLED = false;

// ── Offline pending queue ──────────────────────────────────────────────────────
// When LOGGING_ENABLED = true and an insert fails (e.g. brief offline moment),
// the item is persisted to localStorage and retried via queuePendingActivityLogs().
// Capped at 50 entries — oldest are dropped to prevent unbounded growth.
const PENDING_QUEUE_KEY = "keves_pending_activity_logs";

function _getPendingQueue() {
  try { return JSON.parse(localStorage.getItem(PENDING_QUEUE_KEY) || "[]"); }
  catch { return []; }
}

function _savePendingQueue(queue) {
  try { localStorage.setItem(PENDING_QUEUE_KEY, JSON.stringify(queue.slice(-50))); }
  catch {}
}

/**
 * Append one activity entry to the cloud log.
 * Silently no-ops if logging is disabled or the write fails.
 *
 * @param {Object} params
 * @param {string} params.districtId
 * @param {string} params.activityType  — 'note' | 'visit' | 'prayer' | 'event' | 'person' | 'family'
 * @param {string} [params.sourceId]    — UUID of the originating record
 * @param {string} [params.sourceTable] — source table name
 * @param {string} params.title
 * @param {string} [params.subtitle]
 * @param {string} [params.entityType]
 * @param {string} [params.entityId]
 * @param {string} [params.entityLabel]
 * @param {string} [params.route]
 * @param {Object} [params.metadata]
 * @returns {Promise<void>}
 */
export async function logActivity({
  districtId,
  activityType,
  sourceId,
  sourceTable,
  title,
  subtitle,
  entityType,
  entityId,
  entityLabel,
  route,
  metadata = {},
}) {
  if (!LOGGING_ENABLED) return;
  if (!districtId || !activityType || !title) return;

  const row = {
    district_id:   districtId,
    activity_type: activityType,
    source_id:     sourceId     ?? null,
    source_table:  sourceTable  ?? null,
    title,
    subtitle:      subtitle     ?? null,
    entity_type:   entityType   ?? null,
    entity_id:     entityId     ?? null,
    entity_label:  entityLabel  ?? null,
    route:         route        ?? null,
    metadata,
  };

  try {
    await supabase.from("activity_log").insert(row);
  } catch {
    // Queue for retry on reconnect — logging failures never block the write path
    const q = _getPendingQueue();
    q.push({ ...row, _queued_at: new Date().toISOString() });
    _savePendingQueue(q);
  }
}

/**
 * Fire-and-forget wrapper. Calls logActivity without awaiting — safe to call
 * from synchronous or non-async contexts without unhandled rejection risk.
 * @param {Parameters<typeof logActivity>[0]} params
 */
export function safeLogActivity(params) {
  logActivity(params); // intentionally not awaited
}

/**
 * Flush any activity log entries that failed to insert while offline.
 * Call this from a reconnect handler (e.g. useOfflineStatus onOnline).
 * No-ops when LOGGING_ENABLED = false or while still offline.
 * @param {string} [districtId] — if provided, only flushes entries for that district
 */
export async function queuePendingActivityLogs(districtId) {
  if (!LOGGING_ENABLED) return;
  if (typeof navigator !== "undefined" && !navigator.onLine) return;

  const queue = _getPendingQueue();
  if (queue.length === 0) return;

  const toFlush  = districtId ? queue.filter(i => i.district_id === districtId) : queue;
  const leftover = districtId ? queue.filter(i => i.district_id !== districtId) : [];

  for (const item of toFlush) {
    try {
      const { _queued_at, ...row } = item; // strip internal field
      await supabase.from("activity_log").insert(row);
    } catch {
      leftover.push(item); // keep for next retry
    }
  }

  _savePendingQueue(leftover);
}

/**
 * Convenience: log a note activity.
 * Call this from notes-sync.js after a successful CREATE or UPDATE.
 *
 * @param {Object} note     — raw note object
 * @param {string} districtId
 */
export function logNote(note, districtId) {
  const hasLink = note.linked_type && note.linked_type !== "general";
  return logActivity({
    districtId,
    activityType: "note",
    sourceId:     note.id,
    sourceTable:  "notes",
    title:        note.title || "Nota sin título",
    subtitle:     hasLink && note.linked_label ? `Vinculada · ${note.linked_label}` : undefined,
    entityType:   hasLink ? note.linked_type  : undefined,
    entityId:     hasLink ? note.linked_id    : undefined,
    entityLabel:  hasLink ? note.linked_label : undefined,
    route:        `/notes/${note.id}`,
    metadata:     { category: note.category, is_reference: note.is_reference },
  });
}

/**
 * Convenience: log a visit activity.
 * Call this from visit create/update flows.
 *
 * @param {Object} visit    — raw visit object
 * @param {string} districtId
 */
export function logVisit(visit, districtId) {
  return logActivity({
    districtId,
    activityType: "visit",
    sourceId:     visit.id,
    sourceTable:  "visits",
    title:        visit.contact_name || visit.person_name || "Visita pastoral",
    subtitle:     visit.status === "realizada" ? "Realizada" : "Pendiente",
    entityType:   visit.church_id ? "church" : undefined,
    entityId:     visit.church_id || undefined,
    route:        `/visits/${visit.id}`,
    metadata:     { status: visit.status, church_id: visit.church_id },
  });
}

/**
 * Convenience: log a prayer activity.
 *
 * @param {Object} prayer   — raw prayer object
 * @param {string} districtId
 */
export function logPrayer(prayer, districtId) {
  return logActivity({
    districtId,
    activityType: "prayer",
    sourceId:     prayer.id,
    sourceTable:  "prayers",
    title:        prayer.text || "Petición de oración",
    entityType:   prayer.person_id ? "person" : prayer.family_id ? "family" : undefined,
    entityId:     prayer.person_id || prayer.family_id || undefined,
    route:        `/prayers/${prayer.id}/edit`,
    metadata:     { status: prayer.status, category: prayer.category },
  });
}

/**
 * Convenience: log an event activity (church, field, or personal).
 * Call this from event create flows.
 *
 * @param {Object} event      — raw event object
 * @param {string} districtId
 */
export function logEvent(event, districtId) {
  return logActivity({
    districtId,
    activityType: "event",
    sourceId:     event.id,
    sourceTable:  "events",
    title:        event.title || "Evento ministerial",
    entityType:   event.church_id ? "church" : undefined,
    entityId:     event.church_id || undefined,
    route:        `/agenda`,
    metadata:     { type: event.type || event.event_type, date: event.date || event.start_date },
  });
}
